/**
 * Live entity change for ONE work item: the consolidated `ws:entity_change`
 * channel, narrowed to `work_item` and to this item's id.
 *
 * `useRunLiveFlip` only speaks when a run's status moves, so everything that
 * changes an item without moving a run is invisible to it. The rail proposing a
 * plan amendment onto the open gate is exactly that shape: the gate payload
 * grows a card and no run so much as blinks. Without this the card only reaches
 * a pane that happens to remount, which is the bug this hook exists to close.
 *
 * Sibling of `useRunLiveFlip` on purpose: same window-CustomEvent substrate,
 * same "tell me, I'll re-read" contract, no new protocol. The emitter sets both
 * `entity_id` and `payload.work_item_id` to the item; either one matching is
 * this item, so an emit site that only fills one still reaches the pane.
 */
import { useEffect } from "react";

interface WorkItemEntityChangeDetail {
  entity_id?: unknown;
  payload?: { work_item_id?: unknown } | null;
}

export function useWorkItemEntityChange(
  workItemId: string | null,
  onChange: () => void,
): void {
  useEffect(() => {
    if (!workItemId) return;
    const onEvent = (e: Event) => {
      const detail = (e as CustomEvent<WorkItemEntityChangeDetail>).detail;
      if (!detail) return;
      const matches =
        detail.entity_id === workItemId || detail.payload?.work_item_id === workItemId;
      if (!matches) return;
      onChange();
    };
    window.addEventListener("ws:entity_change:work_item", onEvent);
    return () => {
      window.removeEventListener("ws:entity_change:work_item", onEvent);
    };
  }, [workItemId, onChange]);
}

/**
 * Tell this tab's panes the item changed, the same way the socket tells them.
 *
 * For a write this tab made itself and knows landed (the rail acting on the
 * item): every pane listening for this item re-reads now, without waiting on
 * the server's own entity change to come back round the socket.
 */
export function announceWorkItemChange(workItemId: string): void {
  window.dispatchEvent(
    new CustomEvent("ws:entity_change:work_item", {
      detail: {
        type: "entity_change",
        entity_type: "work_item",
        entity_id: workItemId,
        op: "item_action",
        payload: { work_item_id: workItemId },
      },
    }),
  );
}
